import { LAYER_PLACEMENT } from '../../enums';
import { SECOND_LAYER_SIDE } from '@usercentrics/cmp-browser-sdk';
import { generalStyle } from './MainLayout.helpers';
import { media } from '../../styles';
import styled from 'styled-components';

type BannerContainerProps = {
  showBorderBottom: boolean;
  layerPlacement: LAYER_PLACEMENT | null;
  isAmpEnabled: boolean;
};

export const BannerContainer = styled.div<BannerContainerProps>`
  ${({ showBorderBottom, theme }) => generalStyle(theme, showBorderBottom)}
  ${({ layerPlacement }) => (layerPlacement === LAYER_PLACEMENT.BOTTOM ? 'bottom: 0' : 'top: 0')};
  align-self: center;
  max-width: ${({ theme }) => `${theme.uiSizes.maxBannerWidth}px`};
  max-height: 100%;
  width: 100%;
  height: ${({ isAmpEnabled }) => (isAmpEnabled ? '100%' : 'auto')};
  ${media.mobilePortrait} {
    border-bottom: 0;
  }
  ${media.mobileLandscape} {
    border-bottom: 0;
  }
  ${media.ie11} {
    left: 50%;
    transform: translate(-50%, 0);
  }
`;

type BannerWrapperProps = {
  showBorderBottom: boolean;
  layerPlacement: LAYER_PLACEMENT | null;
  isAmpEnabled: boolean;
};

export const BannerWrapper = styled.div<BannerWrapperProps>`
  ${({ showBorderBottom, theme }) => generalStyle(theme, showBorderBottom)}
  ${({ layerPlacement }) => (layerPlacement === LAYER_PLACEMENT.BOTTOM ? 'bottom: 0' : 'top: 0')};
  background-color: ${({ theme }) => theme.ui.layer.backgroundColor};
  align-items: center;
  box-shadow: 0 3px 7px rgba(0, 0, 0, 0.8);
  left: 0px;
  margin: auto;
  outline: none;
  right: 0;
  width: 100%;
  height: ${({ isAmpEnabled }) => (isAmpEnabled ? '100%' : 'auto')};
  min-height: 180px;
  ${media.mobilePortrait} {
    border-bottom: 0;
    min-height: 280px;
  }
  ${media.mobileLandscape} {
    border-bottom: 0;
    min-height: 280px;
  }
  ${media.ie11} {
    width: 100%;
    min-height: 280px;
  }
`;

type CenterContainerProps = {
  isAmpEnabled: boolean;
  showBorderBottom: boolean;
  withInitialHeight: boolean;
};

type FadingContainerProps = {
  useBackgroundColor: boolean;
};

const resolveHeight = (withInitialHeight: boolean, isAmpEnabled: boolean): string => {
  if (isAmpEnabled) return '100%';

  return withInitialHeight ? 'initial' : 'auto';
};

export const CenterContainer = styled.div<CenterContainerProps>`
  ${({ showBorderBottom, theme }) => generalStyle(theme, showBorderBottom)}
  background-color: ${({ theme }) => theme.ui.layer.backgroundColor};
  align-self: center;
  border-radius: ${({ theme }) => theme.ui.layer.borderRadius};
  box-shadow: ${({ theme }) =>
    theme.ui.useBackgroundShadow ? '0 5px 26px 0px rgba(0, 0, 0, 0.22), 0 20px 28px 0px rgba(0, 0, 0, 0.3)' : 'none'};
  height: ${({ isAmpEnabled, withInitialHeight }) => resolveHeight(withInitialHeight, isAmpEnabled)};
  left: 0;
  margin-left: ${({ isAmpEnabled }) => (isAmpEnabled ? 'initial' : 'auto')};
  margin-right: ${({ isAmpEnabled }) => (isAmpEnabled ? 'initial' : 'auto')};
  max-height: ${({ isAmpEnabled }) => (isAmpEnabled ? 'initial' : '90vh')};
  max-width: ${({ isAmpEnabled, theme }) =>
    isAmpEnabled ? 'initial' : `${theme.uiSizes.maxSideAndCenterContainerWidth}px`};
  outline: none;
  right: 0;
  top: 50%;
  transform: translate(0, -50%);
  width: 100%;

  ${media.mobilePortrait} {
    bottom: 0;
    border-bottom: 0;
    border-radius: ${({ theme }) => theme.ui.layer.borderRadius} ${({ theme }) => theme.ui.layer.borderRadius} 0 0;
    max-width: auto; // IE11 fix
    max-width: initial;
    top: auto;
    transform: initial;
    width: 100%;
  }

  ${media.mobileLandscape} {
    border-bottom: 0;
    max-height: ${({ isAmpEnabled }) => (isAmpEnabled ? 'initial' : '100vh')};
    max-width: ${({ isAmpEnabled, theme }) =>
      isAmpEnabled ? 'initial' : `${theme.uiSizes.maxSideAndCenterContainerWidth}px`};
  }

  ${media.ie11} {
    min-height: 200px;
    top: 50%;
  }

  @supports (-webkit-hyphens: none) {
    max-height: ${({ isAmpEnabled }) => (isAmpEnabled ? 'initial' : '85vh')};
  }
`;

export const RowContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  ${media.mobilePortrait} {
    display: block;
  }
`;

export const ColumnContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  ${media.mobilePortrait} {
    display: block;
  }
`;

export const FadingContainer = styled.div<FadingContainerProps>`
  display: flex;
  flex-direction: column;
  ${({ useBackgroundColor, theme }) => useBackgroundColor && `color: ${theme.ui.text.color}`};
`;

export const VerticalContainer = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.ui.layer.backgroundColor};
  padding: ${({ theme }) => `0 ${theme.spacing.base.xl}`};

  ${media.mobilePortrait} {
    padding: ${({ theme }) => `0 ${theme.spacing.mobile.xxl}`};
  }
`;
export const HorizontalContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
`;

export const MobileContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
`;

export const MobileScrollContent = styled.div<FadingContainerProps>`
  flex: 1 1 auto;
  position: relative;
  overflow-y: auto;
  ${({ useBackgroundColor, theme }) => useBackgroundColor && `color: ${theme.ui.text.color}`};

  /* Hide scrollbar for Chrome, Safari and Opera */
  &.hide-scroll-bar::-webkit-scrollbar {
    display: none;
  }

  /* Hide scrollbar for IE, Edge and Firefox */
  &.hide-scroll-bar {
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */
  }
`;

const fadingStyle = `
  content: '';
  display: block;
  height: 20px;
  pointer-events: none;
  position: absolute;
  width: 100%;
  z-index: 10;
`;

type Props = {
  isMobile?: boolean;
  withFadingTop?: boolean;
  withFadingBottom?: boolean;
};

export const MobileFadingWrapper = styled.div<Props>`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  padding-top: 1px;
  padding-bottom: 1px;
  position: relative;
  overflow: hidden;

  ${media.mobilePortrait} {
    padding-top: 0;
    padding-bottom: 0;
  }

  ${({ withFadingBottom, theme }) =>
    withFadingBottom
      ? `&:after {
    background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, ${theme.ui.secondLayer.content.backgroundColor} 100%);
    bottom: 0;
    ${fadingStyle}
  }`
      : ''}
  ${({ isMobile, withFadingTop }) =>
    withFadingTop || isMobile
      ? `
    &:before {
      background: transparent;
      top: 0;
      ${fadingStyle}
    }
  `
      : ''}

  ${({ theme }) => `&.sticky-fading::before {
    background: linear-gradient(180deg, ${theme.ui.secondLayer.content.backgroundColor} 0%, rgba(255, 255, 255, 0) 100%);
    top: 43px;
    ${fadingStyle}
  }`}
`;

type SideContainerProps = {
  showBorderBottom: boolean;
  side?: SECOND_LAYER_SIDE;
  privacyButtonPosition: string;
};

export const SideContainer = styled.div<SideContainerProps>`
  ${({ showBorderBottom, theme }) => generalStyle(theme, showBorderBottom)}
  background-color: ${({ theme }) => theme.ui.layer.backgroundColor};
  border-radius: ${({ privacyButtonPosition }) => (privacyButtonPosition === 'br' ? '8px 0 0 0' : '0 8px 0 0')};
  bottom: 0;
  box-shadow: 0 3px 7px rgba(0, 0, 0, 0.8);
  height: 100%;
  left: ${({ side }) => (side && side === SECOND_LAYER_SIDE.LEFT ? '0' : 'initial')};
  max-width: ${({ theme }) => `${theme.uiSizes.maxSideAndCenterContainerWidth}px`};
  outline: none;
  padding-bottom: 10px;
  right: ${({ side }) => (side && side === SECOND_LAYER_SIDE.RIGHT ? '0' : 'initial')};
  width: 50%;

  ${media.mobilePortrait} {
    border-bottom: 0;
    transform: initial;
    width: 100%;
    padding-bottom: 0px;
  }

  ${media.mobileLandscape} {
    border-bottom: 0;
    transform: initial;
    width: 95%;
    padding-bottom: 0px;
  }
`;

export const MainElementContainer = styled.div`
  outline: none;
`;
